import type { nl } from "./nl";

// English peer-locale. Same keys as nl.ts (the source of truth for the key
// type); values are free-form strings. NL is default; EN is switchable.
export const en: Record<keyof typeof nl, string> = {
  // ── Navigation ────────────────────────────────────────────────────────────
  "nav.operations": "Operations",
  "nav.dashboard": "Dashboard",
  "nav.proformas": "Proformas",
  "nav.documents": "Documents",
  "nav.administration": "Administration",
  "nav.users": "Users",
  "nav.parties": "Parties",
  "nav.emailAddresses": "Email addresses",
  "nav.mainLabel": "Main navigation",

  // ── Environment badge ─────────────────────────────────────────────────────
  "env.prod": "PROD",
  "env.acc": "ACC",
  "env.local": "LOCAL",

  // ── Non-production banner ─────────────────────────────────────────────────
  "banner.acc": "Acceptance environment",
  "banner.local": "Development environment",
  "banner.note": "proformas and data here are separate from production",

  // ── New version deployed while this tab was open ──────────────────────────
  "newVersion.title": "New version available.",
  "newVersion.body": "Finish what you are doing, then reload the page to keep working.",
  "newVersion.reload": "Reload",

  // ── Shell ─────────────────────────────────────────────────────────────────
  "shell.expandSidebar": "Expand sidebar",
  "shell.collapseSidebar": "Collapse sidebar",
  "shell.help": "Help",
  "shell.notifications": "Notifications",

  // ── User menu ─────────────────────────────────────────────────────────────
  "user.unknownUser": "Unknown user",
  "user.profile": "Profile",
  "user.language": "Language",
  "user.keyboardShortcuts": "Keyboard shortcuts",
  "user.signOut": "Sign out",

  // ── Keyboard shortcuts dialog ─────────────────────────────────────────────
  "kb.title": "Keyboard shortcuts",
  "kb.close": "Close",
  "kb.groups.global": "Global",
  "kb.groups.tables": "Tables",
  "kb.groups.general": "General",
  "kb.shortcuts.openPanel": "Keyboard shortcuts (this panel)",
  "kb.shortcuts.commandPalette": "Command palette",
  "kb.shortcuts.nextRow": "Next row",
  "kb.shortcuts.prevRow": "Previous row",
  "kb.shortcuts.openRow": "Open row",
  "kb.shortcuts.focusSearch": "Focus search",
  "kb.shortcuts.closePanel": "Close panel",

  // ── Login ─────────────────────────────────────────────────────────────────
  "login.title": "Sign in",
  "login.subtitle": "Welcome back. Sign in to the Albert van de Scheur platform.",
  "login.email": "Email address",
  "login.password": "Password",
  "login.forgot": "Forgot?",
  "login.signIn": "Sign in",
  "login.or": "or",
  "login.sso": "Single sign-on (SSO)",
  "login.securedPrefix": "Secured with 2FA · ",
  "login.needHelp": "Need help?",
  "login.brandAlt": "Albert van de Scheur",
  "login.quoteBefore": "Moving with ",
  "login.quoteAccent": "passion",
  "login.quoteAfter": " — since 1934.",
  "login.asideFoot": "Albert van de Scheur · Apeldoorn · Industrial relocations",
  "login.signInFailed": "Sign-in failed. Please try again.",
  "login.devMode": "Development mode — pick roles and sign in",
  "login.signInAsMock": "Sign in (mock)",
  "login.internalTitle": "Internal employees",
  "login.internalSubtitle": "Sign in with your Albert van de Scheur account.",
  "login.partnerTitle": "Partners",
  "login.partnerSubtitle": "Sign in to the partner portal with email and password.",
  "login.partnerUnavailable": "The partner portal is not available yet.",

  // ── Common ────────────────────────────────────────────────────────────────
  "common.thisMonth": "This month",
  "common.previous": "Previous",
  "common.next": "Next",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.viewAll": "View all",
  "common.download": "Download",
  "common.emDash": "—",
  "common.loading": "Loading…",
  "common.retry": "Try again",
  "common.errorTitle": "Something went wrong",
  "common.errorDesc": "The data could not be loaded. Please try again.",

  // ── Proforma (shared) ─────────────────────────────────────────────────────
  "proforma.new": "New proforma",

  // ── Dashboard ─────────────────────────────────────────────────────────────
  "dashboard.greetingMorning": "Good morning, {{name}}",
  "dashboard.greetingAfternoon": "Good afternoon, {{name}}",
  "dashboard.greetingEvening": "Good evening, {{name}}",
  "dashboard.subtitle_one": "{{date}} · {{count}} proforma needs attention",
  "dashboard.subtitle_other": "{{date}} · {{count}} proformas need attention",
  "dashboard.kpi.open": "Open proformas",
  "dashboard.kpi.inProgress": "In progress",
  "dashboard.kpi.revenue": "Revenue this month",
  "dashboard.kpi.onHold": "On hold",
  "dashboard.kpi.openDelta": "▲ 4 vs last week",
  "dashboard.kpi.inProgressDelta": "▲ 2",
  "dashboard.kpi.revenueDelta": "▲ 8.4%",
  "dashboard.kpi.onHoldDelta": "▲ 1 — waiting on customer",
  "dashboard.chart.title": "Requests & revenue",
  "dashboard.chart.subtitle": "Last 8 weeks",
  "dashboard.chart.requests": "Requests",
  "dashboard.chart.revenue": "Revenue (indexed)",
  "dashboard.queue.title": "Work queue",
  "dashboard.alertBefore": "5 proformas on hold. ",
  "dashboard.alertText": "Waiting on documents from the customer. Review the ",
  "dashboard.alertLink": "on-hold list",
  "dashboard.alertAfter": " and send a reminder.",

  // ── Proformas list ────────────────────────────────────────────────────────
  "proformas.title": "Proformas",
  "proformas.summary": "{{total}} total · {{open}} open",
  "proformas.tab.all": "All",
  "proformas.tab.open": "Open",
  "proformas.tab.inProgress": "In progress",
  "proformas.tab.onHold": "On hold",
  "proformas.tab.completed": "Completed",
  "proformas.search": "Search by order number, customer or proforma number…",
  "proformas.filter.workType": "Work type",
  "proformas.selectAll": "Select all",
  "proformas.selectRow": "Select row",
  "proformas.col.number": "Number",
  "proformas.col.customer": "Customer",
  "proformas.col.workType": "Work type",
  "proformas.col.status": "Status",
  "proformas.col.weight": "Weight",
  "proformas.col.amount": "Amount",
  "proformas.col.deliveryDate": "Delivery date",
  "proformas.col.lead": "Project lead",
  "proformas.count": "{{shown}} of {{total}} proformas",
  "proformas.pageOf": "{{page}} / {{pages}}",
  "proformas.empty.title": "No proformas found",
  "proformas.empty.desc": "Adjust your search or filters, or create a new proforma.",
  "proformas.error.title": "Could not load proformas",
  "proformas.error.desc": "Something went wrong while fetching. Try again, or report it with the time it happened.",
  "proformas.error.forbidden.title": "You do not have access to proformas",
  "proformas.error.forbidden.desc": "Ask an administrator for the Project lead role. This is not a missing order — the screen is not allowed to show it.",

  // ── Proforma detail ───────────────────────────────────────────────────────
  "detail.eyebrow": "Proforma · {{number}}",
  "detail.subtitle": "{{location}} · {{weight}} · {{trips}} trips",
  "detail.pdf": "PDF",
  "detail.more": "More",
  "detail.changeStatus": "Change status",
  "detail.changeStatusTitle": "Change status",
  "detail.changeStatusReason": "Reason (optional)",
  "detail.changeStatusConfirm": "Change",
  "detail.noTransitions": "No status change is possible from this state.",
  "detail.transitionFailed": "Status change failed. Please try again.",
  "detail.tab.overview": "Overview",
  "detail.tab.documents": "Documents",
  "detail.tab.timeline": "Timeline",
  "detail.tab.audit": "Audit",
  "detail.card.assignment": "Assignment",
  "detail.card.documents": "Documents",
  "detail.card.activities": "Activities",
  "detail.card.timeline": "Timeline",
  "detail.card.contact": "Contact",
  "detail.addDocument": "Add",
  "detail.kv.workType": "Work type",
  "detail.kv.customer": "Customer",
  "detail.kv.location": "Location",
  "detail.kv.weight": "Weight",
  "detail.kv.trips": "Trips",
  "detail.kv.execution": "Execution",
  "detail.kv.quote": "Quote amount",
  "detail.kv.projectLead": "Project lead",
  "detail.kv.created": "Created",
  "detail.contactRole": "{{company}} · Facility",
  "detail.documentsEmpty": "No documents yet. Documents follow once the module is active.",
  "detail.auditEmpty": "The audit log will appear here once the API is connected.",
  "detail.week": "week {{week}} · {{year}}",
  "detail.trips": "{{count}} trips",

  // ── Intake (4-step) ───────────────────────────────────────────────────────
  "intake.title": "New proforma",
  "intake.subtitle": "Proformas normally arrive from TALIS. Create a draft manually here.",
  "intake.step.customer": "Customer",
  "intake.step.assignment": "Assignment",
  "intake.step.specification": "Specification",
  "intake.step.confirmation": "Confirmation",
  "intake.step1.title": "Step 1 · Customer",
  "intake.step1.subtitle": "Who are we doing the work for?",
  "intake.step2.title": "Step 2 · Assignment",
  "intake.step2.subtitle": "What needs to be moved?",
  "intake.step3.title": "Step 3 · Specification",
  "intake.step3.subtitle": "Weight, planning and specifics.",
  "intake.step4.title": "Step 4 · Confirmation",
  "intake.step4.subtitle": "Review and create the proforma.",
  "intake.f.customer": "Customer",
  "intake.f.customerPlaceholder": "Pick or type a customer…",
  "intake.f.contact": "Contact person",
  "intake.f.workType": "Work type",
  "intake.f.location": "Location",
  "intake.f.description": "Description",
  "intake.f.descriptionPlaceholder": "Describe the assignment…",
  "intake.f.weight": "Estimated weight (t)",
  "intake.f.week": "Preferred week",
  "intake.f.weekHelp": "Subject to planning & equipment availability.",
  "intake.summaryTitle": "Summary",
  "intake.create": "Create proforma",

  // ── Status labels (8 states) ──────────────────────────────────────────────
  "status.concept": "Concept",
  "status.submitted": "Submitted",
  "status.review": "In review",
  "status.approved": "Approved",
  "status.inprogress": "In progress",
  "status.completed": "Completed",
  "status.onhold": "On hold",
  "status.rejected": "Rejected",

  // ── Work types ────────────────────────────────────────────────────────────
  "workType.industrialRelocation": "Industrial relocation",
  "workType.machineTransport": "Machine transport",
  "workType.lifting": "Lifting",
  "workType.storage": "Storage",

  // ── User administration ───────────────────────────────────────────────────
  "users.title": "Users",
  "users.subtitle": "Manage internal employees, roles and access.",
  "users.tab.all": "All",
  "users.tab.active": "Active",
  "users.tab.inactive": "Inactive",
  "users.search": "Search by name or email…",
  "users.filter.role": "All roles",
  "users.col.user": "User",
  "users.col.roles": "Roles",
  "users.col.status": "Status",
  "users.col.lastLogin": "Last login",
  "users.col.created": "Created",
  "users.status.active": "Active",
  "users.status.inactive": "Inactive",
  "users.neverLoggedIn": "Never",
  "users.count": "{{shown}} of {{total}} users",
  "users.pageOf": "{{page}} / {{pages}}",
  "users.empty.title": "No users found",
  "users.empty.desc": "Adjust your search or filter.",
  "users.edit.title": "Edit user",
  "users.edit.status": "Status",
  "users.edit.activeLabel": "Account active",
  "users.edit.selfHint": "You cannot deactivate your own account.",
  "users.edit.roles": "Roles",
  "users.edit.rolesHint": "Determines which parts of the platform this user can access.",
  "users.edit.details": "Details",
  "users.edit.kv.userId": "User ID",
  "users.edit.kv.lastLogin": "Last login",
  "users.edit.kv.created": "Created",
  "users.edit.save": "Save",
  "users.error.generic": "The user could not be updated. Please try again.",
  "users.error.selfDeactivation": "You cannot deactivate your own account.",
  "users.error.lastAdmin": "At least one active administrator must remain.",

  // ── Partys (parties: customers, branches, partners) ──────────────────
  "parties.title": "Parties",
  "parties.subtitle": "Manage customers, branches and partners — the owners of the data.",
  "parties.new": "New party",
  "parties.tab.all": "All",
  "parties.tab.active": "Active",
  "parties.tab.inactive": "Inactive",
  "parties.search": "Search by name or code…",
  "parties.col.name": "Name",
  "parties.col.code": "Code",
  "parties.col.status": "Status",
  "parties.col.created": "Created",
  "parties.status.active": "Active",
  "parties.status.inactive": "Inactive",
  "parties.empty.title": "No parties",
  "parties.empty.desc": "No parties match the current filters yet. Create one to get started.",
  "parties.count": "{{shown}} of {{total}}",
  "parties.pageOf": "Page {{page}} of {{pages}}",
  "parties.create.title": "New party",
  "parties.create.save": "Create",
  "parties.edit.title": "Edit party",
  "parties.edit.save": "Save",
  "parties.edit.status": "Status",
  "parties.edit.activeLabel": "Party is active",
  "parties.edit.activeHelp": "An inactive party is rejected by intake — no new proformas can come in.",
  "parties.edit.details": "Details",
  "parties.edit.kv.id": "ID",
  "parties.edit.kv.created": "Created",
  "parties.edit.kv.updated": "Last updated",
  "parties.field.name": "Name",
  "parties.field.namePlaceholder": "e.g. Tata Steel",
  "parties.field.code": "Code",
  "parties.field.codePlaceholder": "e.g. TATA",
  "parties.field.codeHelp": "2–20 chars: uppercase, digits, dash or underscore. This is the intake key.",
  "parties.field.codeLockedHelp": "The code is the intake key and cannot be changed.",
  "parties.error.duplicate": "A party with this code already exists.",
  "parties.error.validation": "Please check the fields you entered.",
  "parties.error.generic": "The party could not be saved. Please try again.",
  "emailSuggest.header": "Used before for this customer",
  "emailList.remove": "Remove {{email}}",
  "emailList.addAnother": "Add another address…",
  "emailAddresses.title": "Email addresses",
  "emailAddresses.subtitle": "The addresses proformas were sent to, per customer. They are suggested when sending; deactivate a wrong or outdated address to stop that.",
  "emailAddresses.tab.all": "All",
  "emailAddresses.tab.active": "Active",
  "emailAddresses.tab.inactive": "Deactivated",
  "emailAddresses.search": "Search address or customer…",
  "emailAddresses.since.label": "Last used",
  "emailAddresses.since.all": "Any time",
  "emailAddresses.since.month": "This month",
  "emailAddresses.since.prevMonth": "Since last month",
  "emailAddresses.since.90d": "Last 90 days",
  "emailAddresses.col.party": "Customer",
  "emailAddresses.col.email": "Email address",
  "emailAddresses.col.count": "Sent",
  "emailAddresses.col.first": "First used",
  "emailAddresses.col.last": "Last used",
  "emailAddresses.col.status": "Status",
  "emailAddresses.status.active": "Active",
  "emailAddresses.status.inactive": "Deactivated",
  "emailAddresses.deactivate": "Deactivate",
  "emailAddresses.activate": "Activate",
  "emailAddresses.empty.title": "No email addresses",
  "emailAddresses.empty.desc": "An address appears here once a proforma has been sent to it.",
  "emailAddresses.error": "The address could not be updated. Please try again.",

  // ── Roles ─────────────────────────────────────────────────────────────────
  "role.admin": "Administrator",
  "role.planner": "Planner",
  "role.calculation": "Calculation",
  "role.customerService": "Customer service",
  "role.qhse": "QHSE",
  "role.field": "Field engineer",
  "role.projectLead": "Project lead",

  // ── Proforma module (tariff lines, lifecycle) ─────────────────────────────
  "common.edit": "Edit",
  "common.delete": "Delete",
  "common.save": "Save",

  "status.draft": "Draft",
  "status.inReview": "In review",
  "status.reviewed": "Reviewed",
  "status.sent": "Sent",
  "status.invoiced": "Invoiced",
  "status.manuallyInvoiced": "Manually invoiced",

  "section.labor": "Deployment",
  "section.equipment": "Auxiliary equipment",
  "section.transport": "Transport",
  "section.shipping": "Forwarding",
  "section.lifting": "Lifting",
  "section.rental": "Rental",
  "section.other": "Other",

  "vat.standard": "VAT per line",
  "vat.verlegd": "VAT reverse-charged",
  "vat.icp": "Services abroad (ICP)",
  "customerDetail.full": "All lines",
  "customerDetail.fullHint": "Every tariff line with quantity, rate and amount.",
  "customerDetail.sections": "Subtotals",
  "customerDetail.sectionsHint": "One amount per section (Deployment, Transport, …), with VAT and total.",
  "customerDetail.total": "Total only",
  "customerDetail.totalHint": "Only the total amount with VAT.",

  "proformas.orderRef": "Order {{nr}}",
  "proformas.col.work": "Work",
  "proformas.col.total": "Amount (excl. VAT)",
  "proformas.col.sent": "Sent",
  "proformas.col.sentSkipped": "Not sent to the customer — marked ready for invoicing directly",
  "proformas.tab.draft": "To review",
  "proformas.tab.inReview": "In review",
  "proformas.tab.reviewed": "Reviewed",
  "proformas.tab.sent": "Sent",
  "proformas.tab.rejected": "Rejected",
  "proformas.tab.approved": "Approved",
  "proformas.tab.invoiced": "Invoiced",
  "proformas.tab.manuallyInvoiced": "Manual",

  "detail.titleEdit": "Edit title",
  "detail.titleLabel": "Job title",
  "detail.titleHint": "Taken from the first activity's description in TALIS. This title heads the proforma and appears on the customer's PDF.",
  "detail.titleError": "Could not save the title. Please try again.",

  "detail.weekOf": "week of {{date}}",
  "detail.period": "period {{from}} to {{to}}",
  "detail.periodDay": "date {{date}}",
  "detail.pdfSoon": "PDF generation arrives in a later step.",
  "detail.pdfLoading": "PDF…",
  "detail.pdfError": "Could not generate the PDF. Please try again.",
  "detail.send": "Send proforma",
  "detail.sendConfirm": "Send",
  "detail.sendBody": "The proforma is emailed to the customer as a PDF and TALIS is updated (sent).",
  "detail.sendRecipientLabel": "Customer email addresses",
  "detail.sendRecipientHelp": "Pre-filled with the customer's purchasing address. Add addresses with Enter or a comma; everyone gets the same mail with the same link, and whoever responds first decides. Change the address on the proforma to keep it.",
  "detail.sendNoteLabel": "Note (optional)",
  "detail.sendNoteHelp": "Goes into the email to the customer, below the line with the total.",
  "detail.sendReferenceLabel": "Reference / purchase number (optional)",
  "detail.sendReferenceHelp": "Already known? Fill it in once here. The customer finds it pre-filled when approving and can still change it; it goes to TALIS only after approval.",
  "detail.sendFromLabel": "Sender",
  "detail.sendFromSelf": "Send from my own mailbox, with {{lead}} in CC",
  "detail.sendFromLeadHelp": "The email goes out from the mailbox of {{lead}} (project lead).",
  "detail.sendFromSelfHelp": "The email goes out from {{me}}; the project lead gets a copy.",
  "detail.sendFromAdminHelp": "There is no project lead with an email address, so the email goes out from the administration.",
  "detail.sendNoLines": "Add tariff lines first.",
  "detail.registerApprove": "Record approval",
  "detail.registerReject": "Record rejection",
  "detail.rejectedBy": "Rejected by customer:",
  "detail.approvedWith": "Customer approved · reference {{reference}}",
  "detail.readyForInvoicing": "Ready for invoicing",
  "detail.readyForInvoicingBody": "For when the purchase number is already known: the customer is not emailed a proforma. The reference and the tariff lines go straight to TALIS for invoicing.",
  "detail.readyForInvoicingRefHelp": "Leave empty to send the TALIS order number as the reference.",
  "detail.readyForInvoicingConfirm": "Mark ready",
  "detail.readyForInvoicingDone": "Ready for invoicing · reference {{reference}} · not sent to the customer",
  "detail.card.tariff": "Tariff lines",
  "detail.card.details": "Details",
  "detail.tariffEmpty": "No tariff lines yet.",
  "detail.addLine": "Add line",
  "detail.editLine": "Edit line",
  "detail.timeline.refresh_requested": "Refresh requested from TALIS ({{count}} activity/activities)",
  "detail.timeline.intake_reconciled": "Tariff lines updated from TALIS ({{count}} line(s), {{removed}} removed)",
  "detail.timeline.intake_appended": "Tariff lines updated from TALIS ({{count}} line(s))",
  "detail.timeline.line_added": "Line added — {{line}}",
  "detail.timeline.line_changed": "Line changed — {{line}}",
  "detail.timeline.line_removed": "Line removed — {{line}}",
  "detail.timeline.line_moved": "Line moved — {{line}}",
  "detail.timeline.updated": "Proforma changed",
  "detail.field.title": "Title",
  "detail.field.reviewer": "Reviewed by",
  "detail.field.contactEmail": "Customer e-mail",
  "detail.field.contactName": "Contact",
  "detail.field.projectLead": "Project lead",
  "detail.field.locationText": "Location",
  "detail.field.vatMode": "VAT mode",
  "detail.field.customerDetail": "Customer view",

  "detail.field.description": "Description",
  "detail.field.section": "Section",
  "detail.field.kind": "Kind",
  "detail.field.quantity": "Quantity",
  "detail.field.unit": "Unit",
  "detail.field.unitTariffCents": "Tariff",
  "detail.field.pricePer": "Price per",
  "detail.field.amountCents": "Amount",
  "detail.field.vatCode": "VAT",
  "detail.field.employee": "Employee",
  "detail.field.vehicle": "Licence plate",
  "detail.field.date": "Date",
  "detail.field.beginMin": "From",
  "detail.field.endMin": "To",
  "detail.field.pauseMin": "Break",
  "detail.field.totalMin": "Hours",
  "detail.refresh": "Refresh from TALIS",
  "detail.refreshTitle": "Re-request the tariff lines from TALIS — lines added there later will then still come in.",
  "detail.refreshPending": "Requesting…",
  "detail.refreshRequested": "Refresh requested for {{count}} activity/activities. TALIS answers within about a minute; the lines appear on their own.",
  "detail.refreshWarn.title": "Corrections will be lost",
  "detail.refreshWarn.intro": "You corrected {{count}} TALIS line(s) yourself. Refreshing puts them back to what TALIS says now:",
  "detail.refreshWarn.hint": "Should a correction stay? Make it in TALIS first, or do not refresh.",
  "detail.refreshWarn.confirm": "Refresh anyway",
  "detail.refreshError": "The refresh failed. Try again or check the TALIS connection.",
  "detail.lineNotDeletable": "A TALIS line cannot be deleted — zero it out.",
  "detail.subtotal": "Subtotal {{section}}",
  "detail.totals.subtotal": "Subtotal (excl. VAT)",
  "detail.totals.vat": "VAT",
  "detail.totals.total": "Total (incl. VAT)",
  "detail.line.date": "Date",
  "detail.line.groupPlacement": "Placement",
  "detail.line.groupWhat": "What it is",
  "detail.line.groupMoney": "What it costs",
  "detail.line.dragNoGroup": "This line is its own tariff group — there is nothing to reorder it against",
  "detail.line.groupNone": "There is no tariff group on this proforma yet, so this line becomes one. In TALIS that is a new billable unit.",
  "detail.line.dragHint": "Drag to reorder within this tariff group",
  "detail.line.insertHere": "Insert a line here",
  "detail.line.newLine": "New line",
  "detail.line.moveRefused": "The move failed. A tariff line can only be reordered within its own tariff group — TALIS cannot move a line.",
  "detail.gridHint": "Click a field to edit · drag the handle to reorder · + between two rows inserts",
  "detail.line.dateFromActivity": "Date of activity {{id}} — held in TALIS",
  "detail.line.dateNone": "No activity linked",
  "detail.line.employee": "Name",
  "detail.line.vehicle": "Licence plate",
  "detail.line.work": "Work",
  "detail.line.hours": "Hours",
  "detail.line.qty": "Qty",
  "detail.line.tariff": "Tariff",
  "detail.line.vat": "VAT",
  "detail.line.amount": "Amount",
  "detail.line.group": "Belongs to",
  "detail.line.activity": "activity",
  "detail.line.groupNew": "New tariff header",
  "detail.line.groupHint": "The line is filed under this opdrachttarief, in TALIS too.",
  "detail.line.groupNewHint": "TALIS creates a new tariff header — the line then stands apart from the existing ones.",
  "detail.line.section": "Section",
  "detail.line.kind": "Type",
  "detail.line.unit": "Unit",
  "detail.line.amountComputed": "Calculated from qty × tariff",
  "detail.line.amountRecalc": "Recalculate to {{amount}}",
  "detail.kind.tariff": "Tariff",
  "detail.kind.surcharge": "Surcharge",
  "detail.merge": "Merge",
  "detail.mergeConfirm": "Merge",
  "detail.mergeBody": "The tariff lines of {{number}} move to {{target}}. Their activities then stay on this proforma, including after a refresh from TALIS.",
  "detail.mergeWarning": "{{number}} disappears afterwards. A new activity arriving in that week later still gets its own proforma.",
  "detail.mergeError": "The merge failed. Both proformas need the same order and VAT setting, and the source must be a draft that was never sent.",
  "detail.contactTitle": "Proforma recipient",
  "detail.contactEmail": "E-mail address",
  "detail.contactHint": "TALIS does not always hold the right purchasing contact. This address is used when sending.",
  "detail.contactError": "Could not save. Check the e-mail address.",
  "detail.noContactEmail": "No e-mail address known",
  "detail.kv.sameOrder": "Same order",
  "detail.sameOrderHint": "One order gets its own proforma per week.",
  "detail.kv.order": "TALIS order",
  "detail.kv.vatMode": "VAT setting",
  "detail.kv.customerDetail": "Customer view",
  "detail.kv.reference": "Reference",
  "detail.referenceLabel": "Reference / purchase number",
  "detail.referencePlaceholder": "e.g. PO-2026-1234",
  "detail.referenceHelp": "Required. Fed back to TALIS for invoicing.",
  "detail.rejectReasonLabel": "Reason for rejection",
  "detail.rejectReasonHelp": "Required. AvdS-internal only — no message is sent to TALIS.",
  "detail.markManuallyInvoiced": "Manually invoiced",
  "detail.markManuallyInvoicedTitle": "This order was invoiced in TALIS outside the proforma flow",
  "detail.markManuallyInvoicedBody": "This order was invoiced in TALIS the old way. The proforma leaves the work queue and is marked Manually invoiced. Nothing is sent to the customer and nothing is written back to TALIS. This cannot be undone.",
  "detail.markInvoiced": "Mark as invoiced",
  "detail.markInvoicedBody": "The proforma is approved and fed back to TALIS. Mark as invoiced to complete it in AvdS.",
  "detail.vatReverseHelp": "VAT is reverse-charged at proforma level — no per-line override.",
  "detail.actionFailed": "Action failed. Please try again.",
  // A fold always holds at least two entries (TIMELINE_MIN_RUN), so no singular.
  "detail.timeline.foldShow": "Show {{count}} changes",
  "detail.timeline.foldHide": "Hide {{count}} changes",
  "detail.timelineEmpty": "No events yet.",

  "intake.f.title": "Title",
  "intake.f.contactEmail": "Contact email",
  "intake.failed": "Creation failed. Please try again.",

  "dashboard.kpi.toReview": "To review",
  "dashboard.kpi.awaitingRef": "Awaiting reference",
  "dashboard.kpi.rejected": "Rejected",
  "dashboard.kpi.approved": "Approved",
  "dashboard.queue.toReview": "Waiting for my review",
  "dashboard.queue.awaitingRef": "Sent — awaiting reference",
  "dashboard.queueEmpty": "Nothing in this queue.",
  "dashboard.rejectedAlert": "{{count}} proforma(s) rejected by the customer — rework needed. ",
  "dashboard.rejectedLink": "View rejected",
  "dashboard.inReviewAlert": "{{count}} proforma(s) in review (four eyes). ",
  "dashboard.inReviewLink": "View in review",

  // ── Four-eyes principle ───────────────────────────────────────────────
  "review.submit": "Submit for review",
  "review.submitConfirm": "Submit",
  "review.submitIntro": "A colleague checks the proforma before it goes to the customer. By default that is the project lead.",
  "review.submitIntroSelf": "You are the project lead yourself. Choose a colleague to check the proforma.",
  "review.submitIntroNoLead": "This proforma has no project lead with access. Choose a colleague to check it.",
  "review.submitLeadHint": "Project lead",
  "review.submitNoteHelp": "Optional. Included in the notice to the reviewer.",
  "review.noteLabel": "Note",
  "review.reviewer": "Reviewed by",
  "review.reviewerTitle": "Change reviewer",
  "review.reviewerIntro": "For instance when the reviewer is away. The new reviewer gets a notice.",
  "review.requestedBy": "Submitted by {{name}}",
  "review.reviewedBy": "Reviewed by {{name}}",
  "review.someone": "a colleague",
  "review.approve": "Approve",
  "review.approveAndSend": "Approve and send",
  "review.approveBody": "You checked {{asker}}'s proforma and approve it. After that anyone can send it.",
  "review.return": "Send back",
  "review.returnIntro": "The proforma goes back to {{asker}}, with your note.",
  "review.returnIntroEdited": "You changed the proforma. It goes back to {{asker}}, who sees your changes before it is sent.",
  "review.returnNoteHelp": "Required. {{asker}} gets it in a notice and sees it on the proforma.",
  "review.withdraw": "Withdraw",
  "review.withdrawIntro": "The proforma goes back to draft so you can change it again. Then submit it once more.",
  "review.reopen": "Reopen",
  "review.reopenBody": "The proforma goes back to draft. After a change it has to be reviewed again.",
  "review.returnedBanner": "Sent back after review:",
  "review.exemptBanner": "Fixed tariff and unchanged: this proforma may be sent without a review.",
  "review.exemptSentBanner": "This proforma was already sent to the customer: a change does not need another review.",
  "review.bannerAsker": "In review with {{reviewer}}. To change it anyway, withdraw it.",
  "review.bannerReviewer": "{{asker}} asks you to check this proforma. If you change anything, send it back afterwards.",
  "review.bannerEdited": "You made changes. Send the proforma back so {{asker}} sees them before it is sent.",
  "review.bannerReviewed": "Reviewed by {{name}} on {{date}}. The proforma can go to the customer.",
  "review.timelineSubmitted": "In review with {{reviewer}}",
  "review.timelineEdited": "with changes",
  "review.cause.review_returned": "Sent back after review",
  "review.cause.review_withdrawn": "Review withdrawn",
  "review.cause.talis_changed": "Back to draft: TALIS changed the lines",

  // Project lead — from TALIS (ord_orders), changeable in AvdS. A lead without
  // access leaves their proformas in everyone's work queue.
  "lead.filterLabel": "Show proformas of",
  "lead.filter.mine": "My work queue",
  "lead.filter.all": "All proformas",
  "lead.filter.none": "Without a project lead with access",
  "lead.filter.person": "Project lead",
  "lead.none": "nobody",
  "lead.unassigned": "no project lead",
  "lead.nobodyTitle": "Nobody assigned",
  "lead.noAccess": "no access",
  "lead.hint.none": "No project lead in TALIS. In everyone's work queue.",
  "lead.hint.noAccess": "Has no access to AvdS. In everyone's work queue.",
  "lead.hint.manual": "Changed here. According to TALIS: {{talis}}.",
  "lead.emptyMine.title": "Your work queue is empty",
  "lead.emptyMine.desc": "This shows your proformas and those of leads without access.",
  "lead.showAll": "Show all proformas",
  "lead.emptyScoped.mine": "Only your work queue was searched.",
  "lead.emptyScoped.none": "Only proformas without a lead with access were searched.",
  "lead.emptyScoped.person": "Only the proformas of {{name}} were searched.",
  "lead.modal.title": "Project lead",
  "lead.modal.intro": "Decides whose work queue this proforma is in. The customer and TALIS are not affected.",
  "lead.modal.talis": "According to TALIS: {{name}}",
  "lead.modal.talisHint": "Follows TALIS, also when the order gets another lead there later.",
  "lead.modal.error": "Could not change it. Please try again.",

  // ── List tables: columns, sort, filters, pages (components/table) ───────────
  "columns.button": "Columns",
  "columns.buttonHint": "Choose which columns you see and in which order",
  "columns.title": "Columns",
  "columns.alwaysShown": "always shown",
  "columns.hint": "Drag a row or a column header to change the order, or use Alt + ↑/↓. Drag the edge of a column header to make it wider or narrower; double-click the edge to fit it. Your layout and sort are kept with your account.",
  "columns.reset": "Default layout",
  "columns.sortBy": "Sort by {{column}}",
  "columns.dragHint": "Drag to move the column",
  "columns.resize": "Width of {{column}}",
  "columns.resizeHint": "Drag to change the width, double-click to fit",
  "columns.filterBy": "Filter on {{column}}",
  "columns.filteredBy": "Filtered on {{column}}, click to change",
  "columns.filterClear": "Clear",
  "columns.filterDone": "Done",
  "columns.filterContains": "Contains…",
  "columns.filterRangeFrom": "From",
  "columns.filterRangeTo": "Up to and including",
  "columns.filterFrom": "from {{value}}",
  "columns.filterUntil": "up to {{value}}",
  "columns.filteredOn": "Filtered on",
  "columns.filterRemove": "Remove the filter on {{column}}",
  "columns.filterClearAll": "Clear all filters",
  "columns.unitEuro": "€",
  "pager.range": "{{first}}–{{last}} of {{total}}",
  "pager.empty": "No rows",
  "pager.perPage": "Per page",
  "pager.previous": "Previous page",
  "pager.next": "Next page",
  "pager.pageOf": "Page {{page}} of {{pages}}",
  "proformas.col.week": "Week of",
  "proformas.col.reference": "Reference",
  "proformas.col.totalIncl": "Amount (incl. VAT)",
  "proformas.col.created": "Created",
  "proformas.emptyFiltered": "Only the column filters above the table were searched.",

  // Openstaande orders
  "nav.openOrders": "Open orders",
  "openOrders.title": "Open orders",
  "openOrders.subtitle": "Executed, not yet invoiced: every order from O50 (Post-calculation) up to O60 (Invoiceable)",
  "openOrders.syncedAt": "updated from TALIS {{time}}",
  "openOrders.search": "Search order, customer or remark",
  "openOrders.mine": "Only my orders",
  "openOrders.totalOpen": "Open",
  "openOrders.noDate": "No date",
  "openOrders.monthCount_one": "{{count}} order",
  "openOrders.monthCount_other": "{{count}} orders",
  "openOrders.count": "{{shown}} of {{total}} orders",
  "openOrders.col.order": "Order",
  "openOrders.col.customer": "Customer",
  "openOrders.col.lead": "Project lead",
  "openOrders.col.talisRemarks": "Internal remarks (TALIS)",
  "openOrders.col.invoiceText": "Invoice text",
  "openOrders.col.notes": "Internal remarks (AvdS)",
  "openOrders.col.period": "Period",
  "openOrders.col.revenue": "Project revenue",
  "openOrders.col.open": "Open revenue",
  "openOrders.col.state": "Status",
  "openOrders.state.label": "Status",
  "openOrders.state.all": "All statuses",
  "openOrders.state.O50": "Post-calculation",
  "openOrders.state.O51": "Proforma",
  "openOrders.state.O52": "Awaiting purchase number",
  "openOrders.state.O54": "Attention required",
  "openOrders.state.O60": "Invoiceable",
  "openOrders.state.O70": "Completed",
  "openOrders.state.O99": "Cancelled",
  "openOrders.activities": "Activities",
  "openOrders.act.id": "Activity",
  "openOrders.act.date": "Date",
  "openOrders.act.invoiced": "Invoiced",
  "openOrders.proformas": "Proformas",
  "openOrders.note.placeholder": "Add an internal remark",
  "openOrders.note.add": "Add remark",
  "openOrders.note.delete": "Delete remark",
  "openOrders.note.none": "No internal remarks yet.",
  "openOrders.note.error": "Saving the remark failed. Please try again.",
  "openOrders.empty.title": "No open orders",
  "openOrders.empty.desc": "No order sits between Post-calculation and Invoiceable, or the filters exclude everything.",
  // Route van een activiteit (docs/route-distance.md)
  "detail.route.km": "{{km}} km",
  "detail.route.open": "Route on the map",
  "detail.route.title": "Route {{ref}}",
  "detail.route.loading": "Calculating route…",
  "detail.route.error": "The route could not be calculated.",
  "detail.route.kilometers": "Kilometres",
  "detail.route.exact": "{{km}} km by road, rounded up to 5",
  "detail.route.sentToTalis": "Sent to TALIS when the ride was reported ready, {{date}}",
  "detail.route.screenOnly": "Calculated for this screen only. When the ride is reported ready, AvdS calculates again and the kilometres go to TALIS.",
  "detail.route.role.home": "Home",
  "detail.route.role.load": "Load",
  "detail.route.role.unload": "Unload",
  "detail.route.source.talis": "TALIS",
  "detail.route.source.here": "HERE",
  "detail.route.reason.not_configured": "HERE is not configured",
  "detail.route.reason.no_address": "No address on the activity",
  "detail.route.reason.load_not_found": "Load address not found",
  "detail.route.reason.unload_not_found": "Unload address not found",
  "detail.route.reason.home_not_found": "Home address not found",
};
